/**
 * Typed API client for the Workers API.
 *
 * Base URL is configured via VITE_API_URL environment variable.
 * Centralizes fetch logic, error handling, and content-type headers.
 */

import type {
	AdminStatsResponse,
	CreatorFeesResponse,
	CreatorTokenListResponse,
	FeeRevenueHistoryResponse,
	HolderEntry,
	HolderHistoryEntry,
	IndexedResponse,
	PaginatedTokenList,
	PlatformStats,
	PriceCandle,
	PriceInterval,
	Token,
	TokenDetailResponse,
	TokenResponse,
	TradeHistoryEntry,
	TrendingTokensResponse,
	VerificationStatus,
	VolumeHistoryEntry,
} from "@repo/shared-types";

type ApiErrorBody = {
	error?: {
		message?: string;
	};
};

const API_BASE = import.meta.env.VITE_API_URL ?? "";

/**
 * Resolve an API-relative path (e.g. "/v1/uploads/abc") to a full URL.
 * Needed because asset URLs from the API (like logo URLs) are relative to the API origin,
 * but the frontend runs on a different origin in development.
 */
export function resolveApiAssetUrl(path: string | null | undefined): string | null {
	if (!path) return null;
	if (path.startsWith("http://") || path.startsWith("https://")) return path;
	return `${API_BASE}${path}`;
}

function isApiErrorBody(data: unknown): data is ApiErrorBody {
	if (typeof data !== "object" || data === null) return false;
	if (!("error" in data)) return false;
	// After "error" in data, TS narrows to Record<string, unknown>
	const { error } = data;
	if (typeof error !== "object" || error === null) return false;
	return "message" in error;
}

/**
 * Throw an error with a descriptive message from the API response.
 * TanStack Query requires thrown errors for queryFn error handling.
 */
async function throwApiError(response: Response): Promise<never> {
	const body: unknown = await response.json().catch(() => null);
	let message = `API error: ${String(response.status)}`;
	if (isApiErrorBody(body) && body.error?.message) {
		message = body.error.message;
	}
	// eslint-disable-next-line no-restricted-syntax -- TanStack Query requires thrown errors for queryFn error handling
	throw new Error(message);
}

/**
 * Parse a successful JSON response.
 */
async function parseJsonResponse<T>(response: Response): Promise<T> {
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Generic JSON deserialization boundary; response shape validated at call sites
	return (await response.json()) as T;
}

/**
 * Build auth headers for authenticated API requests.
 * Centralizes JWT token and wallet address injection.
 */
function buildAuthHeaders(token?: string, walletAddress?: string): Record<string, string> {
	const headers: Record<string, string> = {};
	if (token) {
		headers.Authorization = `Bearer ${token}`;
	}
	if (walletAddress) {
		headers["X-Wallet-Address"] = walletAddress;
	}
	return headers;
}

async function fetchApi<T>(path: string, options?: RequestInit): Promise<T> {
	const response = await fetch(`${API_BASE}${path}`, {
		...options,
		headers: {
			"Content-Type": "application/json",
			...options?.headers,
		},
	});

	if (!response.ok) {
		return throwApiError(response);
	}

	return parseJsonResponse<T>(response);
}

/**
 * Fetch helper for multipart/form-data requests.
 * Does NOT set Content-Type — the browser sets the multipart boundary automatically.
 */
async function fetchApiFormData<T>(
	path: string,
	formData: FormData,
	options?: RequestInit
): Promise<T> {
	const response = await fetch(`${API_BASE}${path}`, {
		method: "POST",
		...options,
		headers: {
			...options?.headers,
		},
		body: formData,
	});

	if (!response.ok) {
		return throwApiError(response);
	}

	return parseJsonResponse<T>(response);
}

type UploadResponse = {
	id: string;
	url: string;
};

export const platformApi = {
	getStats: (): Promise<{ data: PlatformStats }> =>
		fetchApi<{ data: PlatformStats }>("/v1/platform/stats"),
};

export const uploadsApi = {
	uploadLogo: (
		file: File,
		token: string,
		walletAddress?: string
	): Promise<{ data: UploadResponse }> => {
		const formData = new FormData();
		formData.append("file", file);
		return fetchApiFormData<{ data: UploadResponse }>("/v1/uploads", formData, {
			headers: buildAuthHeaders(token, walletAddress),
		});
	},
};

/**
 * API client params for token listing.
 * Uses string types because URL query params are string-encoded.
 * Server validates and narrows via ListTokensQuerySchema from @repo/shared-types.
 */
export type TokenListParams = {
	search?: string;
	category?: string;
	sort?: string;
	order?: string;
	verification?: string;
	cursor?: string;
	limit?: number;
};

export const tokensApi = {
	list: (params: TokenListParams): Promise<{ data: PaginatedTokenList }> => {
		const searchParams = new URLSearchParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== "") {
				searchParams.set(key, String(value));
			}
		}
		const qs = searchParams.toString();
		return fetchApi<{ data: PaginatedTokenList }>(`/v1/tokens${qs ? `?${qs}` : ""}`);
	},
	register: (
		request: unknown,
		token: string,
		walletAddress?: string
	): Promise<{ data: TokenResponse }> => {
		return fetchApi<{ data: TokenResponse }>("/v1/tokens", {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				...buildAuthHeaders(token, walletAddress),
			},
			body: JSON.stringify(request),
		});
	},
	getById: (id: string): Promise<{ data: TokenResponse }> =>
		fetchApi<{ data: TokenResponse }>(`/v1/tokens/${id}`),
	trending: (limit = 10): Promise<{ data: TrendingTokensResponse }> => {
		const params = new URLSearchParams({ limit: String(limit) });
		return fetchApi<{ data: TrendingTokensResponse }>(`/v1/tokens/trending?${params.toString()}`);
	},
	getDetail: (id: string): Promise<{ data: TokenDetailResponse }> =>
		fetchApi<{ data: TokenDetailResponse }>(`/v1/tokens/${id}/detail`),
	getPriceHistory: (
		id: string,
		interval: PriceInterval,
		limit?: number
	): Promise<{ data: IndexedResponse<PriceCandle[]> }> => {
		const params = new URLSearchParams({ interval });
		if (limit !== undefined) params.set("limit", String(limit));
		return fetchApi<{ data: IndexedResponse<PriceCandle[]> }>(
			`/v1/tokens/${id}/price-history?${params.toString()}`
		);
	},
	getTrades: (
		id: string,
		limit?: number
	): Promise<{ data: IndexedResponse<TradeHistoryEntry[]> }> => {
		const params = new URLSearchParams();
		if (limit !== undefined) params.set("limit", String(limit));
		const qs = params.toString();
		return fetchApi<{ data: IndexedResponse<TradeHistoryEntry[]> }>(
			`/v1/tokens/${id}/trades${qs ? `?${qs}` : ""}`
		);
	},
	getHolders: (id: string, limit?: number): Promise<{ data: IndexedResponse<HolderEntry[]> }> => {
		const params = new URLSearchParams();
		if (limit !== undefined) params.set("limit", String(limit));
		const qs = params.toString();
		return fetchApi<{ data: IndexedResponse<HolderEntry[]> }>(
			`/v1/tokens/${id}/holders${qs ? `?${qs}` : ""}`
		);
	},
	getVolumeHistory: (
		id: string,
		interval: PriceInterval,
		limit?: number
	): Promise<{ data: IndexedResponse<VolumeHistoryEntry[]> }> => {
		const params = new URLSearchParams({ interval });
		if (limit !== undefined) params.set("limit", String(limit));
		return fetchApi<{ data: IndexedResponse<VolumeHistoryEntry[]> }>(
			`/v1/tokens/${encodeURIComponent(id)}/volume-history?${params.toString()}`
		);
	},
	getHolderHistory: (
		id: string,
		interval: PriceInterval,
		limit?: number
	): Promise<{ data: IndexedResponse<HolderHistoryEntry[]> }> => {
		const params = new URLSearchParams({ interval });
		if (limit !== undefined) params.set("limit", String(limit));
		return fetchApi<{ data: IndexedResponse<HolderHistoryEntry[]> }>(
			`/v1/tokens/${encodeURIComponent(id)}/holder-history?${params.toString()}`
		);
	},
};

export const creatorApi = {
	getTokens: (token: string, walletAddress?: string): Promise<{ data: CreatorTokenListResponse }> =>
		fetchApi<{ data: CreatorTokenListResponse }>("/v1/creator/tokens", {
			headers: buildAuthHeaders(token, walletAddress),
		}),
	getTokenFees: (
		tokenId: string,
		token: string,
		walletAddress?: string
	): Promise<{ data: CreatorFeesResponse }> =>
		fetchApi<{ data: CreatorFeesResponse }>(`/v1/creator/tokens/${tokenId}/fees`, {
			headers: buildAuthHeaders(token, walletAddress),
		}),
	getRevenueHistory: (
		tokenId: string,
		interval: PriceInterval,
		token: string,
		walletAddress?: string,
		limit?: number
	): Promise<{ data: FeeRevenueHistoryResponse }> => {
		const params = new URLSearchParams({ interval });
		if (limit !== undefined) params.set("limit", String(limit));
		return fetchApi<{ data: FeeRevenueHistoryResponse }>(
			`/v1/creator/tokens/${encodeURIComponent(tokenId)}/revenue-history?${params.toString()}`,
			{ headers: buildAuthHeaders(token, walletAddress) }
		);
	},
};

export type AdminTokenListParams = {
	search?: string;
	verification?: string;
	cursor?: string;
	limit?: number;
};

export const adminApi = {
	getStats: (token: string): Promise<{ data: AdminStatsResponse }> =>
		fetchApi<{ data: AdminStatsResponse }>("/v1/admin/stats", {
			headers: buildAuthHeaders(token),
		}),

	getTokens: (
		params: AdminTokenListParams,
		token: string
	): Promise<{ data: { tokens: Token[]; hasMore: boolean } }> => {
		const searchParams = new URLSearchParams();
		for (const [key, value] of Object.entries(params)) {
			if (value !== undefined && value !== "") {
				searchParams.set(key, String(value));
			}
		}
		const qs = searchParams.toString();
		return fetchApi<{ data: { tokens: Token[]; hasMore: boolean } }>(
			`/v1/admin/tokens${qs ? `?${qs}` : ""}`,
			{ headers: buildAuthHeaders(token) }
		);
	},

	verifyToken: (
		tokenId: string,
		body: { status: VerificationStatus; reason?: string },
		token: string
	): Promise<{ data: { token: Token; previousStatus: string } }> => {
		// H3: Validate tokenId format to prevent path traversal
		if (!/^[a-zA-Z0-9_-]+$/.test(tokenId)) {
			// eslint-disable-next-line no-restricted-syntax -- Defensive validation
			throw new Error("Invalid token ID format");
		}
		return fetchApi<{ data: { token: Token; previousStatus: string } }>(
			`/v1/admin/tokens/${tokenId}/verify`,
			{
				method: "POST",
				headers: buildAuthHeaders(token),
				body: JSON.stringify(body),
			}
		);
	},
};

export type { UploadResponse };
